import { useEffect, useMemo, useState } from 'react';
import { usePatientProfile } from '../../hooks/patient/usePatientProfile';
import { useLastPrescription } from '../../hooks/prescription/useLastPrescription';
import { useUserPrescriptions } from '../../hooks/prescription/useUserPrescriptions';
import { useMedicationGuide } from '../../hooks/prescription/useMedicationGuide';
import { usePharmInsightGuide } from '../../hooks/recommendation/usePharmInsightGuide';
import useAuthStore from '../../../core/stores/authStore';
import LogUtil from '../../../core/utils/LogUtil';
import { PatientHeader } from './components/PatientHeader';
import { AiRecommendationContent } from './components/AiRecommendationContent';
import { MedicationGuideContent } from './components/MedicationGuideContent';
import { PrescriptionTabContent } from './components/PrescriptionTabContent';

export {
  PatientHeader,
  AiRecommendationContent,
  MedicationGuideContent,
  PrescriptionTabContent,
}; // backward-compatible

const MAIN_TABS = [
  { id: 'ai', label: 'AI 맞춤 추천' },
  { id: 'guide', label: '복약지도문' },
  { id: 'rx', label: '처방전' },
];

const rxMeds = rx => rx?.analysis?.medications || rx?.medications_resolved || rx?.medications || [];

/* 헤더 처방 행: 약명 / ATC(성분) / 1일 N회 · N일 — 최근 처방 1건 기준 */
const toHeaderMed = m => {
  const atc = m.atc_code || m.atc;
  const ingredient = m.ingredient || m['성분'] || m.ingredient_name;
  const freq = m.frequency_per_day ?? m['1일투약횟수'];
  const days = m.total_days ?? m['총투약일수'];
  return {
    name: m.medication_name || m.name || m['약품명'] || '-',
    atc: atc ? (ingredient ? `${atc}(${ingredient})` : atc) : '',
    schedule: [freq != null && `1일 ${freq}회`, days != null && `${days}일`].filter(Boolean).join(' · '),
  };
};

const PatientDetailPanel = ({ userId, patient }) => {
  const [mainTab, setMainTab] = useState('ai');
  const brn = useAuthStore(s => s.pharmacy?.business_registration_number);

  // 데이터 fetch는 hook으로 (캐싱 + loading/error 자동)
  const detailQuery = usePatientProfile(userId);
  const lastRxQuery = useLastPrescription(userId);
  const prescriptionListQuery = useUserPrescriptions(userId, { limit: 20 });

  // 처방전 리스트의 모든 medications에서 EDI 코드 추출 → 복약지도문 / 팜인사이트 안내 호출
  const ediCodes = useMemo(() => {
    const items = prescriptionListQuery.data ?? [];
    return Array.from(new Set(
      items.flatMap(rxMeds)
        .map(m => m.edi_code || m.insurance_code || m.medication_code || m.code)
        .filter(Boolean)
    ));
  }, [prescriptionListQuery.data]);
  const guideQuery = useMedicationGuide(ediCodes);
  const insightQuery = usePharmInsightGuide(ediCodes, brn);

  const detail = detailQuery.data ?? null;
  const lastRx = lastRxQuery.data ?? null;
  const prescriptionList = prescriptionListQuery.data ?? [];
  const guideMeds = guideQuery.data ?? [];
  const insight = insightQuery.data ?? null;

  const headerMeds = useMemo(() => {
    const meds = rxMeds(lastRx).length ? rxMeds(lastRx) : rxMeds(prescriptionList[0]);
    return meds.map(toHeaderMed);
  }, [lastRx, prescriptionList]);
  // 질환군: 안내 API diseases[{name, atc_l3[]}] — 약 성분(ATC) 기준 질환군, Figma '고혈압(C09C)' (백엔드 10/23).
  // 빈 배열이면 행 숨김 (상병코드 대체 표시는 쓰지 않음)
  const diseaseGroups = useMemo(
    () =>
      (insight?.diseases || [])
        .filter(d => d?.name)
        .map(d => (d.atc_l3?.length ? `${d.name}(${d.atc_l3.join(', ')})` : d.name)),
    [insight]
  );

  // 에러 로깅 (기존 동작 유지)
  useEffect(() => { if (detailQuery.error) LogUtil.error('[PatientDetailPanel] detail 실패:', detailQuery.error); }, [detailQuery.error]);
  useEffect(() => { if (lastRxQuery.error) LogUtil.error('[PatientDetailPanel] 마지막 처방전 조회 실패:', lastRxQuery.error); }, [lastRxQuery.error]);
  useEffect(() => { if (prescriptionListQuery.error) LogUtil.error('[PatientDetailPanel] 처방전 리스트 실패:', prescriptionListQuery.error); }, [prescriptionListQuery.error]);
  useEffect(() => { if (guideQuery.error) LogUtil.error('[PatientDetailPanel] 복약지도문 실패:', guideQuery.error); }, [guideQuery.error]);
  useEffect(() => { if (insightQuery.error) LogUtil.error('[PatientDetailPanel] 팜인사이트 안내 실패:', insightQuery.error); }, [insightQuery.error]);

  const merged = useMemo(
    () => ({ ...(patient || {}), ...(detail || {}) }),
    [patient, detail]
  );

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-white">
      <PatientHeader patient={merged} diseaseGroups={diseaseGroups} medications={headerMeds} />

      {/* 메인 탭 — Figma 767:26754: pt4 px24 border-b, 탭 gap 24 */}
      <nav className="flex shrink-0 items-end gap-6 px-6 pt-1 border-b border-zinc-200">
        {MAIN_TABS.map(t => {
          const active = mainTab === t.id;
          const color = active ? '#1d356d' : '#a1a1aa';
          return (
            <button
              key={t.id}
              onClick={() => setMainTab(t.id)}
              className="flex items-center gap-2 justify-center py-1.5 transition-colors"
              style={{
                borderBottom: active ? '2px solid #1d356d' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              <span style={{ fontSize: 'var(--text-16)', fontWeight: 600, color, lineHeight: '22px', letterSpacing: '-0.32px' }}>
                {t.label}
              </span>
              {t.id === 'rx' && (
                <span style={{ fontSize: 'var(--text-18)', fontWeight: 500, color, lineHeight: 1.35, letterSpacing: '-0.36px' }}>
                  {prescriptionList.length}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Section_container — Figma 767:26756: p24 */}
      <div className="flex flex-1 flex-col p-6">
        {mainTab === 'ai' && (
          <AiRecommendationContent guide={insight} loading={insightQuery.isLoading} />
        )}
        {mainTab === 'guide' && (
          <MedicationGuideContent guideMeds={guideMeds} />
        )}
        {mainTab === 'rx' && (
          <PrescriptionTabContent
            prescriptions={prescriptionList}
            merged={merged}
          />
        )}
      </div>
    </div>
  );
};

export default PatientDetailPanel;
